import React, { useState, useEffect, useRef, useContext } from "react";
import { ReactComponent as BellIcon } from "assets/icons/bell-black.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { Button, NotificationSubscriptionCategory, SubscriptionSection } from "modules/common";
import { updateNotificationSubscription } from "modules/dashboard";
import { ToggleSwitch } from "../ToggleSwitch";
import { useGetNotificationSubscriptionsForCategory } from "services";
import { useDispatch } from "react-redux";
import { ReactComponent as Spinner } from "assets/icons/spinner.svg";
import { ReactComponent as ChevronIcon } from "assets/icons/chevron-down-dark-gray.svg";
import { AccessibilityContext } from "views/AccessibilityContext";
import { classNames } from "helpers/ClassNameHelper";

interface SubscriptionMenuProps {
    subscriptionCategories: SubscriptionSection[];
}

export const SubscriptionMenu: React.FC<SubscriptionMenuProps> = ({ subscriptionCategories }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const [activatedSubscriptions, setActivatedSubscriptions] = useState<string[]>([]);
    const dispatch = useDispatch();
    const subscriptionQueries = useGetNotificationSubscriptionsForCategory(
        isOpen,
        subscriptionCategories
    );
    const isLoading = subscriptionQueries.some(query => query.isLoading);

    useEffect(() => {
        if (subscriptionQueries.every(query => query.isSuccess)) {
            const data = subscriptionQueries.map((query, index) => ({
                category: subscriptionCategories[index].subscriptions[0]?.category,
                subscriptions: query.data ?? [],
            }));

            const activeSubscriptions = data.flatMap(({ category, subscriptions }) =>
                subscriptions.map((subId: number) => `${category}_${subId}`)
            );

            setActivatedSubscriptions(activeSubscriptions);
        }
    }, [...subscriptionQueries.map(q => q.data)]);

    useEffect(() => {
        if (!isOpen) {
            subscriptionQueries.forEach(query => query.refetch());
        }
    }, [isOpen]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const [category, idString] = value.split("_");
        const id = parseInt(idString);

        dispatch(updateNotificationSubscription(category as NotificationSubscriptionCategory, id));

        const subscriptionKey = `${category}_${id}`;
        setActivatedSubscriptions(prev =>
            prev.includes(subscriptionKey)
                ? prev.filter(subscription => subscription !== subscriptionKey)
                : [...prev, subscriptionKey]
        );
    };

    const hasAnyActiveSubscription = activatedSubscriptions.length > 0;

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("click", handleOutsideClick);
        }

        return () => {
            document.removeEventListener("click", handleOutsideClick);
        };
    }, [isOpen]);

    const { lowVision } = useContext(AccessibilityContext);

    return (
        <div className="subscription-menu-container">
            <Button
                isIcon
                onClick={() => setIsOpen(!isOpen)}
                variant={hasAnyActiveSubscription ? "primary" : "primary-ghost"}
                size={"small"}
            >
                <BellIcon
                    className={`bell-image ${hasAnyActiveSubscription ? "bell-image-invert" : ""}`}
                />
            </Button>

            {isOpen && (
                <div className={classNames("subscription-menu", lowVision && "low-vision-darkgray")} ref={menuRef}>
                    <div className="menu-header">
                        <span className="subscriptions-title">Predbilježbe</span>
                        <button className="close-button" onClick={() => setIsOpen(false)}>
                            <CloseIcon className="close-icon" />
                        </button>
                    </div>
                    <hr />

                    <div className="expansion-panels">
                        {isLoading ? (
                            <div className="loading-state">
                                <Spinner className="spinner spinner-small" />
                            </div>
                        ) : (
                            subscriptionCategories.map((group, index) => (
                                <details key={index} className="expansion-panel">
                                    <summary className="category-text">
                                        <span className="category-title">{group.groupTitle}</span>
                                        <ChevronIcon className="chevron-icon" />
                                    </summary>
                                    <div className="subscriptions-list">
                                        {group.subscriptions.map(subscription => (
                                            <div
                                                key={subscription.id}
                                                className="subscription-item"
                                            >
                                                <span className="subscriptions-prompt-text">
                                                    {subscription.title}
                                                </span>
                                                <ToggleSwitch
                                                    htmlFor={`${subscription.category}_${subscription.id}`}
                                                    value={`${subscription.category}_${subscription.id}`}
                                                    onChange={handleChange}
                                                    checked={activatedSubscriptions.includes(
                                                        `${subscription.category}_${subscription.id}`
                                                    )}
                                                    className="subscription-row__toggle"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionMenu;
